import { Add } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { FC } from 'react';

import Footer from '@components/DrawerContent/Footer';
import Title from '@components/DrawerContent/Title';

import { xorUsers } from '../../helpers';
import { DrawerContentPropsMultiSelect, TabValues } from '../../types';
import LocationRecipients from '../LocationRecipients';
import UsersList from '../UsersList';
import { useMultiSelectUsers } from './useMultiSelectUsers';

const MultiSelect: FC<DrawerContentPropsMultiSelect> = ({
  model,
  onClose,
  onSave,
  selected,
  deleted,
  additionalAllParams,
  additionalSelectedParams,
  noSelected,
  noItemsSetMessage,
  title,
  checkForExistingLocation,
}) => {
  const {
    itemsCount,
    activeTab,
    handleTabChange,
    tempSelected,
    selectedUsers,
    onAddUserClick,
    deletedUsers,
    onRemoveUserClick,
    onApplyUsersClick,
    applySelectedUsers,
    onCancelClick,
    usersWithLocation,
    isLocationConfirmationOpen,
    setIsLocationConfirmationOpen,
    hasNewUsersWithLocation,
  } = useMultiSelectUsers({
    noSelected,
    selected,
    deleted,
    filters: additionalSelectedParams,
    checkForExistingLocation,
  });

  const onApplyClick = () => onSave({ selectedUsers, deletedUsers });

  const isSelectedTab = activeTab === TabValues.SELECTED_USERS;

  return (
    <Box display="flex" flexDirection="column" flexGrow={1} bgcolor="common.white">
      <Title
        count={
          isSelectedTab ? itemsCount - Object.keys(deletedUsers).length + Object.keys(selectedUsers).length : undefined
        }
        title={isSelectedTab ? title || model : `Add ${model}`}
        onClose={onClose}
        rightAdornment={
          isSelectedTab && (
            <Button
              variant="text"
              color="primary"
              size="medium"
              startIcon={<Add />}
              sx={{ minWidth: 'fit-content' }}
              onClick={() => handleTabChange(TabValues.ALL_USERS)}
            >
              Add {model}
            </Button>
          )
        }
      />

      <UsersList
        key={+isSelectedTab}
        isSelected={isSelectedTab}
        model={model}
        selectedUsers={isSelectedTab ? selectedUsers : xorUsers(selectedUsers, tempSelected)}
        deletedUsers={deletedUsers}
        onItemClick={isSelectedTab ? onRemoveUserClick : onAddUserClick}
        filters={isSelectedTab ? additionalSelectedParams : additionalAllParams}
        skipRequest={isSelectedTab && noSelected}
        noItemsSetMessage={noItemsSetMessage}
      />

      {isSelectedTab ? (
        <Footer handleSubmit={onApplyClick} sx={{ m: 2.5 }} />
      ) : (
        <Footer
          handleSubmit={onApplyUsersClick}
          title={`Add Selected`}
          sx={{ m: 2.5, display: 'flex', gap: 2 }}
          onCancel={onCancelClick}
        />
      )}

      {checkForExistingLocation && hasNewUsersWithLocation() && (
        <LocationRecipients
          isOpened={isLocationConfirmationOpen}
          setIsOpened={setIsLocationConfirmationOpen}
          usersToConfirm={usersWithLocation}
          onConfirmationClick={applySelectedUsers}
        />
      )}
    </Box>
  );
};

export default MultiSelect;
